// Shared period-lock helpers for the match drawer (PRO-12200): the lock icon
// on source/selected/search rows, and the lock-gated Match/Unmatch/Confirm
// actions all key off the same "is this item's own date inside its entity's
// locked period" definition, so they can't silently disagree about what
// counts as locked.
import { useMemo } from "react";
import { toDisplayDateOnly } from "@helpers/date";
import { getLockDate, isLockedByDateOnly } from "@helpers/lock";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { useLockListState } from "@api/lock.api";
import { MatchableItem, MatchItemType } from "./types";

const LOCK_LABEL_NOUN_BY_TYPE: Partial<Record<MatchItemType, string>> = {
    [MatchItemType.TRANSACTION]: "transaction",
    [MatchItemType.INVOICE]: "invoice",
    [MatchItemType.BILL]: "bill",
};

export function getMatchItemLockedLabel(type: MatchItemType): string {
    return `This ${LOCK_LABEL_NOUN_BY_TYPE[type] ?? "item"} is locked.`;
}

export const MATCH_ITEM_LOCKED_TOOLTIP =
    "This item is in a locked accounting period. To unlock, go to settings > locks.";

export const MATCH_CONFIRM_LOCKED_REASON =
    "One of the matched parties is in a locked accounting period";

export interface LockCheckItem {
    entityId: number;
    type: MatchItemType;
    dateOnly?: string | null;
    date?: Date;
}

/**
 * The item's own accounting day (`YYYY-MM-DD`) used for the lock comparison, or
 * `null` when it can't be determined yet. Only a transaction's `date` is its
 * accounting day; a document's (invoice/bill) `date` is its *creation*
 * timestamp (see the mappers and `buildStubsFromExistingMatches`), so a
 * suggested/stub document selected before its details load has `createdAt` but
 * no `dateOnly`. We must not treat that creation day as its accounting day —
 * such a document returns `null` ("date unknown") rather than silently keying
 * the lock off the wrong date.
 */
function resolveDateOnly(item: LockCheckItem): string | null {
    if (item.dateOnly) {
        return item.dateOnly;
    }
    return item.type === MatchItemType.TRANSACTION
        ? toDisplayDateOnly(item.date)
        : null;
}

/**
 * True when a single row's own date falls inside its own entity's locked
 * period. Used per table row (each row cell is its own component instance,
 * so one hook call here is not a "hooks in a loop" violation).
 */
export function useIsMatchableItemLocked(item: LockCheckItem): boolean {
    return useIsDateLocked(item.entityId, resolveDateOnly(item));
}

/**
 * Pure core of `useIsAnyMatchableItemLocked`, split out so the aggregation
 * logic (per-item entity lookup + date comparison) is unit-testable without
 * rendering a hook. True when any item's own date falls inside its own
 * entity's locked period.
 */
export function computeIsAnyMatchableItemLocked(
    items: LockCheckItem[],
    lockDateByEntityId: ReadonlyMap<number, Date | null>,
): boolean {
    return items.some((item) => {
        const lockDate = lockDateByEntityId.get(item.entityId) ?? null;
        if (!lockDate) {
            return false;
        }
        const dateOnly = resolveDateOnly(item);
        // The entity has a locked period, but this item's accounting date hasn't
        // loaded yet (a suggested/stub document before enrichment). It can't be
        // proven to sit outside the lock, so it blocks the match rather than
        // defaulting to "unlocked" — which would let a locked party through the
        // Confirm gate until its `dateOnly` arrives.
        if (!dateOnly) {
            return true;
        }
        return isLockedByDateOnly(lockDate, dateOnly);
    });
}

/**
 * True when any of the given items (e.g. the source transaction plus every
 * currently selected match) falls inside its own entity's locked period.
 * Fetches lock dates for the whole set in one query (keyed on the sorted,
 * deduped entity ids) instead of one `useIsDateLocked` call per item, which
 * would otherwise require calling a hook a variable number of times.
 */
export function useIsAnyMatchableItemLocked(items: LockCheckItem[]): {
    isConfirmBlocked: boolean;
    lockedReason: string | null;
} {
    const entityIds = useMemo(
        () =>
            [...new Set(items.map((item) => item.entityId))].sort(
                (left, right) => left - right,
            ),
        [items],
    );
    const { locks, isResolved } = useLockListState(entityIds);
    const lockDateByEntityId = useMemo(() => {
        const map = new Map<number, Date | null>();
        for (const lock of locks) {
            map.set(lock.entityId, getLockDate([lock]));
        }
        return map;
    }, [locks]);

    // Until the lock query resolves (while loading, or if it fails,
    // `useLockListState` supplies an empty list), assume a party *might* be
    // locked and keep Confirm disabled rather than letting a match through that
    // the period lock is meant to block. The locked-period reason stays hidden
    // until a lock is actually confirmed, so it doesn't flash while loading.
    if (!isResolved) {
        return { isConfirmBlocked: true, lockedReason: null };
    }

    const hasLockedParty = computeIsAnyMatchableItemLocked(
        items,
        lockDateByEntityId,
    );
    return {
        isConfirmBlocked: hasLockedParty,
        lockedReason: hasLockedParty ? MATCH_CONFIRM_LOCKED_REASON : null,
    };
}

export function matchableItemToLockCheckItem(
    item: Pick<MatchableItem, "entityId" | "type" | "dateOnly" | "date">,
): LockCheckItem {
    return {
        entityId: item.entityId,
        type: item.type,
        dateOnly: item.dateOnly,
        date: item.date,
    };
}
